import { CompositeLayer, UpdateParameters } from "@deck.gl/core";
import { BitmapLayer } from "@deck.gl/layers";
import {
  TileLayer,
  _Tile2DHeader as Tile,
  _TileLoadProps as TileLoadProps,
} from "@deck.gl/geo-layers";

import NoCacheTileset2D from "./no-cache-tileset-2d";

export type BoundingBox = {
  north: number;
  east: number;
  south: number;
  west: number;
};

export type TileInfo = {
  id: string;
  tileSize: number;
  bbox: BoundingBox;
  index: { x: number; y: number; z: number };
};

export type GetTileImageProps = {
  tile: TileInfo;
  signal?: AbortSignal;
};

export type TileChangeSet = {
  tiles: TileInfo[];
  addedTiles: TileInfo[];
  removedTiles: TileInfo[];
};

export type TiledDeckGlLayerProps = {
  getTileImage: (props: GetTileImageProps) => Promise<HTMLImageElement | undefined>;
  slot?: string;
  tileSize: number;
  extent?: BoundingBox;
  minZoom?: number;
  maxZoom?: number;

  onTilesChanged?: (changeSet: TileChangeSet) => void;
};

const DEBOUNCE_TIME = 100;

export class TiledDeckGlLayer extends CompositeLayer<TiledDeckGlLayerProps> {
  declare state: {
    tiles: Tile[];
  };

  updateState({ changeFlags }: UpdateParameters<TiledDeckGlLayer>) {
    if (changeFlags.somethingChanged) this.updateTilesFromTileset();
  }

  shouldUpdateState({ changeFlags }: UpdateParameters<TiledDeckGlLayer>) {
    return changeFlags.somethingChanged;
  }

  renderLayers() {
    return [this.renderTileLayer()];
  }

  private renderTileLayer() {
    return new TileLayer({
      TilesetClass: NoCacheTileset2D,

      ...this.getSubLayerProps({
        id: "tile-layer",
        updateTriggers: {
          getTileData: this.props.updateTriggers.getTileImage,
        },
      }),
      slot: this.props.slot,
      tileSize: this.props.tileSize,
      minZoom: this.props.minZoom,
      maxZoom: this.props.maxZoom,
      extent: this.props.extent
        ? [
            this.props.extent.west,
            this.props.extent.south,
            this.props.extent.east,
            this.props.extent.north,
          ]
        : undefined,

      refinementStrategy: "no-overlap",

      // The maxRequests variable is assigned a large value in order to
      // enable all calls to the getTileData function (which is used to
      // replace the contents of the entire viewport) to run simultaneously.
      // This approach prevents a slow and unresponsive update when the
      // currentTick changes.
      //
      // It is not possible to set maxRequests to -1 because doing so
      // causes issues with the debounceTime setting.
      maxRequests: 1000,

      debounceTime: DEBOUNCE_TIME,
      getTileData: this.getTileData.bind(this),
      renderSubLayers: (props) => {
        if (!props.data) return;
        return this.renderTileImage(props.tile, props.data, props);
      },
    });
  }

  private async getTileData({ id, bbox, index, signal }: TileLoadProps) {
    return await this.props.getTileImage({
      tile: this.getTileInfo(id, bbox as BoundingBox, index),
      signal,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private renderTileImage(tile: Tile, image: any, props: any) {
    const bbox = tile.boundingBox;

    return new BitmapLayer(props, {
      data: undefined,
      image,
      bounds: [bbox[0][0], bbox[0][1], bbox[1][0], bbox[1][1]],
    });
  }

  private updateTilesFromTileset() {
    const layer = this.getSubLayers()?.find((layer) => layer instanceof TileLayer);
    const tiles = layer?.state.tileset?.selectedTiles;

    if (!tiles) return;

    let addedTiles = [] as Tile[];
    let removedTiles = [] as Tile[];

    if (!this.state.tiles) {
      addedTiles = tiles;
    } else {
      addedTiles = tiles.filter((tile) => !this.state.tiles.includes(tile));
      removedTiles = this.state.tiles.filter((tile) => !tiles.includes(tile));
    }

    if (addedTiles.length === 0 && removedTiles.length === 0) return;

    const changeSet = {
      tiles: tiles.map((tile) => this.getTileInfoForTile(tile)),
      addedTiles: addedTiles.map((tile) => this.getTileInfoForTile(tile)),
      removedTiles: removedTiles.map((tile) => this.getTileInfoForTile(tile)),
    };

    if (!this.state.tiles) {
      this.onTilesChanged(this, changeSet);
    } else {
      this.debouncedOnTilesChanged(this, changeSet);
    }

    this.setState({ tiles: tiles });
  }

  private debouncedOnTilesChanged = debounce(DEBOUNCE_TIME, this.onTilesChanged);
  private onTilesChanged(layer: TiledDeckGlLayer, changeSet: TileChangeSet) {
    layer.props.onTilesChanged?.(changeSet);
  }

  private getTileInfoForTile(tile: Tile): TileInfo {
    return this.getTileInfo(tile.id, tile.bbox as BoundingBox, tile.index);
  }

  private getTileInfo(
    tileId: string,
    bbox: BoundingBox,
    index: TileInfo["index"],
  ): TileInfo {
    return {
      id: tileId,
      bbox,
      tileSize: this.props.tileSize,
      index,
    };
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
function debounce<T extends Function>(wait: number, cb: T) {
  let h = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const callable = (...args: any) => {
    clearTimeout(h);
    h = window.setTimeout(() => cb(...args), wait);
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <T>(<any>callable);
}

TiledDeckGlLayer.layerName = "TiledLayer";
